import { useState, useRef, useEffect, useCallback } from 'react'
import { router, usePage, Link } from '@inertiajs/react'
import { Loader2 } from 'lucide-react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Input } from '@/components/ui/input'
import { PageProps } from '@/types'

type VerifyProps = {
  email?: string
  code_expires_in_minutes: number
}

export default function SessionsVerify({ email, code_expires_in_minutes }: VerifyProps) {
  const { flash } = usePage<PageProps>().props
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [processing, setProcessing] = useState(false)
  const [shake, setShake] = useState(false)
  const [lastShakeId, setLastShakeId] = useState<number | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const submittedCodeRef = useRef<string | null>(null)

  useEffect(() => {
    const shakeId = typeof flash.shake === 'number' ? flash.shake : null
    if (shakeId && shakeId !== lastShakeId) {
      setLastShakeId(shakeId)
      setShake(true)
      setDigits(['', '', '', '', '', ''])
      submittedCodeRef.current = null
      requestAnimationFrame(() => {
        inputRefs.current[0]?.focus()
      })
      setTimeout(() => setShake(false), 400)
    }
  }, [flash.shake, lastShakeId])

  const submitCode = useCallback((code: string) => {
    if (processing || submittedCodeRef.current === code) return
    submittedCodeRef.current = code
    setProcessing(true)
    router.post('/session/magic_link', { code }, {
      onFinish: () => setProcessing(false),
      onError: () => { submittedCodeRef.current = null }
    })
  }, [processing])

  useEffect(() => {
    const code = digits.join('')
    if (code.length === 6 && digits.every(d => d !== '') && !shake && !processing) {
      submitCode(code)
    }
  }, [digits, submitCode, shake, processing])

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...digits]
    newDigits[index] = digit
    setDigits(newDigits)
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length > 0) {
      const newDigits = [...digits]
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || ''
      }
      setDigits(newDigits)
      inputRefs.current[Math.min(pasted.length, 5)]?.focus()
    }
  }

  return (
    <AuthLayout
      title="Verify"
      heading="Check your inbox"
      description={email ? `Enter the 6-digit code we sent to ${email}` : 'Enter the 6-digit code we sent to your email'}
    >
      <div className="space-y-6">
        <div
          className={`flex justify-center gap-2 sm:gap-2.5 ${shake ? 'animate-shake' : ''}`}
          onPaste={handlePaste}
          role="group"
          aria-label="Verification code"
        >
          {digits.map((digit, index) => (
            <Input
              key={index}
              ref={el => { inputRefs.current[index] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              className="w-12 h-14 text-center text-xl font-bold rounded-lg border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              autoFocus={index === 0}
              disabled={processing}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Sent to {email || 'your email'}. Good for {code_expires_in_minutes} minutes, then
          ask for another.
        </p>

        {flash.magic_link_code && (
          <div className="p-3 bg-amber-50 border border-amber-200/80 rounded-lg text-center">
            <p className="text-[10px] text-amber-500 font-semibold uppercase tracking-widest mb-1">Dev Mode</p>
            <p className="text-2xl font-mono font-bold tracking-[0.3em] text-amber-700">{flash.magic_link_code}</p>
          </div>
        )}

        {processing && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="animate-spin h-4 w-4" />
            Verifying...
          </div>
        )}

        <div className="pt-4 text-center space-y-1">
          <p className="text-[13px] text-muted-foreground">
            Can't find it? Check your spam folder.
          </p>
          <Link
            href="/session/new"
            className="inline-block text-[13px] text-primary font-semibold hover:underline underline-offset-2"
          >
            Use a different email
          </Link>
        </div>
      </div>

    </AuthLayout>
  )
}
